import { cleanCloudinaryUrl, removeQueryString } from '#pplx/frontend/common/files/urls.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { subscribeAttachmentsBatch } from '#pplx/frontend/components/hooks/attachments.js';
import { uploadConnectorFile } from '#pplx/frontend/components/hooks/connectorAttachmentUpload.js';
import { fileExtension, fileUploadSourceType, } from '#pplx/frontend/components/hooks/fileUploadAnalytics.js';
import { terminalFromAttachmentProcessing } from '#pplx/frontend/components/hooks/useUploadFiles.js';
import { ATTACHMENT_FAILURE_MESSAGES } from '#pplx/frontend/components/utils/attachmentsUtils.js';
import { BLOCKED_UPLOAD_MIME_TYPES, BYTES_PER_MB, FALLBACK_MAX_FILE_UPLOAD_SIZE_MB, MIN_FILE_UPLOAD_SIZE, } from '#pplx/frontend/components/utils/consts.js';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
/** Uses the connector transport without downloading provider documents into browser Files. */
export function useUploadCloudFiles({ reason, defaultFileSource, maxFileSizeMB = FALLBACK_MAX_FILE_UPLOAD_SIZE_MB, skipMimeValidation, uploadRateLimit, onRateLimitUpdate, analytics, }) {
    const { $t } = useIntl();
    const rejectFiles = useCallback((files, errorCode) => {
        files.forEach((file) => analytics?.rejectInitiation([{ name: file.name, size: file.sizeBytes }], fileUploadSourceType(file.provider), errorCode, files.length));
    }, [analytics]);
    const uploadFiles = useCallback(async (documents, onChange, signal) => {
        if (signal?.aborted)
            return [];
        const context = {
            $t,
            analytics,
            defaultFileSource,
            maxFileSizeMB,
            onRateLimitUpdate,
            reason,
            signal,
            skipMimeValidation,
            uploadRateLimit,
        };
        const batch = createCloudUploadBatch(documents, context, onChange);
        batch.start();
        signal?.addEventListener('abort', batch.abort, { once: true });
        try {
            await Promise.all(batch.entries.map((entry) => uploadCloudFile(entry, batch, context)));
        }
        finally {
            signal?.removeEventListener('abort', batch.abort);
        }
        return batch.entries.map((entry) => ({ ...entry }));
    }, [
        $t,
        analytics,
        defaultFileSource,
        maxFileSizeMB,
        onRateLimitUpdate,
        reason,
        skipMimeValidation,
        uploadRateLimit,
    ]);
    return { rejectFiles, uploadFiles };
}
/** Keeps mutable transport entries private while publishing snapshots to the consumer. */
function createCloudUploadBatch(documents, { $t, analytics, signal }, onChange) {
    const entries = documents.map((document) => ({
        id: safeRandomUUID(),
        cloudDocument: document,
        file: new File([], document.name, { type: document.mimeType ?? '' }),
        fileName: document.name,
        fileType: document.mimeType ?? '',
        fileSize: document.sizeBytes ?? 0,
        status: 'uploading',
    }));
    const attempts = new Map(entries.map((entry) => [
        entry.id,
        {
            uploadAttemptUUID: entry.id,
            fileUUID: null,
            sourceType: fileUploadSourceType(entry.cloudDocument.provider),
            fileSizeBytes: entry.cloudDocument.sizeBytes ?? null,
            fileExtension: fileExtension(entry.fileName),
            fileCount: entries.length,
        },
    ]));
    const complete = (entry, outcome, errorCode) => {
        const attempt = attempts.get(entry.id);
        if (!attempt)
            return;
        attempts.delete(entry.id);
        analytics?.completed({ ...attempt, fileUUID: entry.serverId ?? null }, outcome, errorCode);
    };
    const update = (entry, patch) => {
        if (signal?.aborted)
            return;
        Object.assign(entry, patch);
        onChange?.([{ ...entry }]);
    };
    const fail = (entry, code, error = $t(ATTACHMENT_FAILURE_MESSAGES.generic_upload_error)) => {
        update(entry, { status: 'failed', failureCode: code, error });
        complete(entry, 'failure', code);
    };
    const start = () => {
        attempts.forEach((attempt) => analytics?.started(attempt));
        // Register the whole selection before starting requests so queued submit and reset see it.
        onChange?.(entries.map((entry) => ({ ...entry })));
    };
    const abort = () => entries.forEach((entry) => complete(entry, 'failure', 'client_no_response'));
    return { entries, start, update, complete, fail, abort };
}
/** Checks provider metadata without treating its placeholder File as empty local content. */
function validateCloudFile(document, fileCount, { $t, maxFileSizeMB, skipMimeValidation, uploadRateLimit }) {
    if (document.sizeBytes !== undefined && document.sizeBytes > maxFileSizeMB * BYTES_PER_MB) {
        return {
            code: 'file_too_large',
            error: $t(ATTACHMENT_FAILURE_MESSAGES.too_large, { max: `${maxFileSizeMB} MB` }),
        };
    }
    if (!document.id ||
        !document.name ||
        (document.sizeBytes && document.sizeBytes < MIN_FILE_UPLOAD_SIZE) ||
        (!skipMimeValidation && BLOCKED_UPLOAD_MIME_TYPES.includes(document.mimeType ?? ''))) {
        return { code: 'unsupported_type', error: $t(ATTACHMENT_FAILURE_MESSAGES.unsupported_type) };
    }
    if (uploadRateLimit !== undefined && fileCount > uploadRateLimit) {
        return { code: 'rate_limited' };
    }
    return undefined;
}
/** Observes server processing, including rejection and streams ending without a result. */
async function processCloudFile(entry, fileUUID, batch, { reason, signal }) {
    await subscribeAttachmentsBatch({
        request: { file_uuids: [fileUUID] },
        reason,
        onMessage: (data) => {
            if (signal?.aborted || data.file_uuid !== entry.serverId)
                return;
            const result = terminalFromAttachmentProcessing(data);
            if (result.outcome === 'failure') {
                batch.fail(entry, result.errorCode ?? 'index_failed');
                return;
            }
            const url = data.s3_url ?? entry.url;
            const previewUrl = entry.previewUrl !== undefined &&
                url !== undefined &&
                cleanCloudinaryUrl(removeQueryString(entry.previewUrl)) ===
                    cleanCloudinaryUrl(removeQueryString(url))
                ? entry.previewUrl
                : undefined;
            batch.update(entry, { status: 'success', url, previewUrl });
            batch.complete(entry, 'success');
        },
    });
    if (entry.status === 'parsing')
        batch.fail(entry, 'client_no_response');
}
/** Validates one selection and adapts the shared connector result into its batch state. */
async function uploadCloudFile(entry, batch, context) {
    const document = entry.cloudDocument;
    const validationError = validateCloudFile(document, batch.entries.length, context);
    if (validationError) {
        batch.fail(entry, validationError.code, validationError.error);
        return;
    }
    const { analytics, defaultFileSource, onRateLimitUpdate, reason, signal } = context;
    try {
        const terminal = await uploadConnectorFile({
            request: {
                remote_file_id: document.id,
                connection_type: document.provider.toUpperCase(),
                source: defaultFileSource,
                reason,
                analytics: analytics ? { uploadAttemptUUID: entry.id } : undefined,
            },
            signal,
            onUploaded: ({ url, originalUrl, filename, fileUUID, status }) => {
                onRateLimitUpdate?.(1);
                batch.update(entry, {
                    url,
                    previewUrl: originalUrl,
                    fileName: filename,
                    serverId: fileUUID,
                    status: fileUUID ? status : 'success',
                });
            },
            onProcessing: (fileUUID) => processCloudFile(entry, fileUUID, batch, context),
        });
        if (signal?.aborted)
            return;
        switch (terminal.outcome) {
            case 'aborted':
                return;
            case 'failure':
                batch.fail(entry, terminal.errorCode);
                break;
            case 'success':
                batch.complete(entry, 'success');
                break;
            default:
                assertUnreachableValue(terminal);
        }
    }
    catch {
        if (!signal?.aborted)
            batch.fail(entry, 'client_no_response');
    }
}
//# sourceMappingURL=useUploadCloudFiles.js.map